import { useState, useRef, useEffect } from 'react';

const services = [
  {
    id: 1,
    title: 'Projeto de Interiores',
    description: 'Desenvolvemos projetos completos de design de interiores, desde o conceito até os detalhes técnicos. Cada ambiente é pensado para refletir sua personalidade com funcionalidade e estética refinada.',
    icon: 'ri-home-heart-line',
    image: 'https://readdy.ai/api/search-image?query=elegant%20modern%20interior%20design%20living%20room%20architectural%20drawing%20blueprint%20planning%20sophisticated%20minimalist%20apartment%20design%20studio%20professional&width=600&height=400&seq=serv001&orientation=landscape',
    features: ['Planta baixa detalhada', 'Projeto 3D', 'Memorial descritivo', 'Especificação de materiais'],
  },
  {
    id: 2,
    title: 'Gerenciamento de Obra',
    description: 'Acompanhamos cada etapa da sua reforma com rigor técnico e atenção aos detalhes. Garantimos que o projeto saia do papel exatamente como planejado, dentro do prazo e do orçamento.',
    icon: 'ri-tools-line',
    image: 'https://readdy.ai/api/search-image?query=construction%20site%20management%20interior%20renovation%20apartment%20modern%20professional%20architect%20overseeing%20work%20quality%20control%20sophisticated&width=600&height=400&seq=serv002&orientation=landscape',
    features: ['Cronograma de obra', 'Controle de qualidade', 'Gestão de fornecedores', 'Relatórios periódicos'],
  },
  {
    id: 3,
    title: 'Consultoria',
    description: 'Para quem precisa de orientação especializada sem um projeto completo. Oferecemos consultoria personalizada para decisões de reforma, escolha de materiais e otimização de espaços.',
    icon: 'ri-lightbulb-line',
    image: 'https://readdy.ai/api/search-image?query=interior%20design%20consultation%20meeting%20client%20architect%20discussing%20plans%20samples%20materials%20swatches%20elegant%20professional%20office%20sophisticated&width=600&height=400&seq=serv003&orientation=landscape',
    features: ['Análise do espaço', 'Orientação de materiais', 'Paleta de cores', 'Soluções práticas'],
  },
];

export default function Servicos() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-s').forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = '1';
                (el as HTMLElement).style.transform = 'translateY(0)';
              }, i * 150);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const prev = () => setActiveIndex((i) => (i === 0 ? services.length - 1 : i - 1));
  const next = () => setActiveIndex((i) => (i === services.length - 1 ? 0 : i + 1));

  return (
    <section id="servicos" className="py-24 md:py-32 bg-[#FAF8FA]">
      <div ref={sectionRef} className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-16 md:mb-20">
          <div className="reveal-s opacity-0 translate-y-6 transition-all duration-700" style={{ transitionProperty: 'opacity, transform' }}>
            <span className="inline-flex items-center gap-2 px-4 py-1.5 border border-[#D6C4D6] text-[#301531] font-montserrat text-xs font-semibold tracking-[0.2em] uppercase rounded-full mb-5">
              O que fazemos
            </span>
          </div>
          <div className="reveal-s opacity-0 translate-y-6 transition-all duration-700" style={{ transitionProperty: 'opacity, transform' }}>
            <h2 className="font-gothic text-[#301531] leading-tight" style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)' }}>
              Nossos Serviços
            </h2>
          </div>
          <div className="reveal-s opacity-0 translate-y-6 transition-all duration-700" style={{ transitionProperty: 'opacity, transform' }}>
            <p className="font-montserrat text-[#BCBDBF] text-base mt-4 max-w-xl mx-auto leading-relaxed">
              Soluções completas em arquitetura de interiores para transformar seu espaço com planejamento estratégico
            </p>
          </div>
        </div>

        {/* Desktop: 3 cards side by side */}
        {!isMobile ? (
          <div className="grid grid-cols-3 gap-8">
            {services.map((service, idx) => (
              <ServiceCard key={service.id} service={service} delay={idx * 150} />
            ))}
          </div>
        ) : (
          /* Mobile: Carousel */
          <div className="relative">
            <div className="overflow-hidden">
              <div
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${activeIndex * 100}%)` }}
              >
                {services.map((service) => (
                  <div key={service.id} className="w-full flex-shrink-0 px-2">
                    <ServiceCard service={service} delay={0} />
                  </div>
                ))}
              </div>
            </div>

            {/* Arrows */}
            <button
              onClick={prev}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-12 h-12 bg-white rounded-full flex items-center justify-center cursor-pointer"
              style={{ boxShadow: '0 4px 16px rgba(48,21,49,0.12)' }}
              aria-label="Anterior"
            >
              <i className="ri-arrow-left-s-line text-[#301531] text-xl" />
            </button>
            <button
              onClick={next}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-12 h-12 bg-white rounded-full flex items-center justify-center cursor-pointer"
              style={{ boxShadow: '0 4px 16px rgba(48,21,49,0.12)' }}
              aria-label="Próximo"
            >
              <i className="ri-arrow-right-s-line text-[#301531] text-xl" />
            </button>

            {/* Dots */}
            <div className="flex justify-center gap-2 mt-8">
              {services.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActiveIndex(i)}
                  className={`rounded-full transition-all duration-300 cursor-pointer ${
                    i === activeIndex ? 'w-6 h-2 bg-[#301531]' : 'w-2 h-2 bg-[#D6C4D6]'
                  }`}
                  aria-label={`Ir para serviço ${i + 1}`}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

interface ServiceCardProps {
  service: typeof services[0];
  delay: number;
}

function ServiceCard({ service, delay }: ServiceCardProps) {
  return (
    <div
      className="reveal-s opacity-0 translate-y-8 bg-white rounded-3xl overflow-hidden group hover:-translate-y-2 transition-all duration-500 cursor-default"
      style={{
        transitionProperty: 'opacity, transform, box-shadow',
        boxShadow: '0 4px 24px rgba(48,21,49,0.06)',
        transitionDelay: `${delay}ms`,
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.boxShadow = '0 16px 48px rgba(48,21,49,0.12)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 24px rgba(48,21,49,0.06)';
      }}
    >
      {/* Image */}
      <div className="relative overflow-hidden" style={{ height: '220px' }}>
        <img
          src={service.image}
          alt={service.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#301531]/40 to-transparent" />
        <div className="absolute bottom-4 left-4 w-12 h-12 flex items-center justify-center bg-white/90 rounded-xl">
          <i className={`${service.icon} text-[#301531] text-xl`} />
        </div>
      </div>

      {/* Content */}
      <div className="p-8">
        <h3 className="font-gothic text-[#301531] text-xl mb-3">{service.title}</h3>
        <p className="font-montserrat text-[#1A1A1A] text-sm leading-relaxed mb-6">{service.description}</p>

        {/* Features */}
        <ul className="space-y-2">
          {service.features.map((feature) => (
            <li key={feature} className="flex items-center gap-2 font-montserrat text-xs text-[#BCBDBF]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#D6C4D6] flex-shrink-0" />
              {feature}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
